import { jwtDecode } from "jwt-decode";
import baseAxios, {
  AxiosError,
  InternalAxiosRequestConfig,
  isAxiosError,
} from "axios";
import { UnauthenticatedUserError, UnexpectedError } from "./errors";
import { addBreadcrumb } from "@sentry/browser";

const axios = baseAxios.create();
interface BidJwtPayload {
  jti: string;
  sub: string;
  aud: string;
  iss: string;
  exp: number;
  iat: number;
  nbf: number;
  kind: "access" | "refresh";
}

function randomDelay() {
  return new Promise((resolve) => {
    setTimeout(resolve, Math.random() * 3000);
  });
}
axios.interceptors.request.use((request) => {
  // Adds a header when communicating with the bff API. This header is used
  // on the backend to use a mocked http session instead of the regular one
  if (
    import.meta.env.VITE_ADD_MOCK_HTTP_SESSION_HEADER &&
    import.meta.env.DEV
  ) {
    console.log("Adding mock HTTP session header");
    request.headers["x-mock-http-sessions"] = "1";
  }
  return request;
});

axios.interceptors.request.use(
  (request) => {
    addBreadcrumb({
      category: "http-request",
      data: {
        method: request.method,
        url: request.url,
      },
    });
    const jwtPayload: BidJwtPayload | null = JSON.parse(
      localStorage.getItem("jwtPayload") || "null"
    );
    const accessToken = localStorage.getItem("accessToken");
    if (!jwtPayload || !accessToken) {
      return Promise.reject(
        new UnauthenticatedUserError(
          "No access token or JWT payload available."
        )
      );
    }
    request.baseURL = jwtPayload?.iss || import.meta.env.VITE_BACKEND_BASE_URL;
    request.headers.Authorization = `Bearer ${accessToken}`;
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    addBreadcrumb({
      category: "http-response",
      data: {
        method: response.config.method,
        url: response.config.url,
        status_code: response.status,
        response_body: JSON.stringify(response.data),
      },
    });
    return response;
  },
  async (error: AxiosError) => {
    if (isAxiosError(error) && error.response) {
      addBreadcrumb({
        category: "http-response",
        data: {
          method: error.config?.method,
          url: error.config?.url,
          status_code: error.response.status,
          response_body: JSON.stringify(error.response.data),
        },
      });

      if (error.response.status === 400) {
        Promise.reject(
          new UnexpectedError(
            error.message,
            error.code,
            error.config,
            error.request,
            error.response
          )
        );
      }
    }

    const originalRequest = (error?.config || {
      _alreadyRetried: false,
      _retries: 0,
    }) as InternalAxiosRequestConfig<unknown> & {
      _alreadyRetried: boolean;
      _retries: number;
    };

    // App runner intermitency retry
    const appRunnerErrors = [429, 502, 503, 504];
    const maxRetries = 3;
    if (appRunnerErrors.includes(error?.response?.status || 0)) {
      const currentRetries = (originalRequest._retries || 0) + 1;
      if (currentRetries > maxRetries) {
        return Promise.reject(error);
      }
      originalRequest._retries = currentRetries;
      await randomDelay();
      return axios(originalRequest);
    }

    if (error.response?.status === 401) {
      removeCurrentUserToken();
      window.location.href = "/usuarios/login";
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export function seemsAuthenticated() {
  return Boolean(localStorage.getItem("accessToken"));
}

export function setCurrentUserToken({ access }: { access: string }) {
  sessionStorage.removeItem("loanEntryOperatorId");
  sessionStorage.removeItem("filterByLoanEntryOperatorId");
  sessionStorage.removeItem("themeColors");
  sessionStorage.removeItem("themeLogo");
  localStorage.setItem("accessToken", access);
  localStorage.setItem("jwtPayload", JSON.stringify(jwtDecode(access)));
}

export function removeCurrentUserToken() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("jwtPayload");
}

export default axios;
